<template>
  <div>
    <item-availability-modal :item="selectedItemForAvailability" />
    <items-form
      :category="selectedCategory"
      :item="selectedItem"
      @done="fetchCategories"
      @hidden="itemsFormHiddenHandler"
    />
    <delete-category-modal
      :category="toBeDeletedCategory"
      @category-deleted="fetchCategories"
    />
    <delete-item-modal
      :item="toBeDeletedItem"
      @item-deleted="fetchCategories"
    />
    <div class="d-flex justify-content-end mb-1">
      <b-button
        variant="flat-primary"
        @click="showCategoriesForm = !showCategoriesForm"
      >
        <feather-icon
          :icon="showCategoriesForm ? 'XIcon' : 'PlusCircleIcon'"
          size="20"
        />
      </b-button>
    </div>
    <CategoriesForm
      v-if="showCategoriesForm"
      @created="() => {
        showCategoriesForm = false;
        fetchCategories()
      }"
    />
    <b-spinner
      v-if="fetchingCategories"
      variant="primary"
    />
    <h6 v-if="!fetchingCategories && !categories.length">
      No Categories yet..
    </h6>
    <CardCollapse
      v-for="category in categories"
      :key="category.id"
      @show="fetchCategoryItems(category)"
    >
      <template #card-title>
        <div style="flex:1">
          <h3
            v-if="!category.editMode"
            class="mb-1"
          >
            {{ category.name.en + ' / '+ category.name.ar }}
            <small>( {{ snakeCaseToCamelCase(category.state) }} )</small>
            <small class="text-muted">{{ category.requestedChange && '(Pending Changes)' }}</small>
          </h3>
          <div
            v-else
            class="d-flex mb-1"
          >
            <b-form-input
              v-model="category.editModeCat.en"
              placeholder="en"
              class="mr-2"
            />
            <b-form-input
              v-model="category.editModeCat.ar"
              placeholder="ar"
              class="mr-2"
            />
            <b-button
              :disabled="!category.editModeCat.en || !category.editModeCat.ar || category.editingCategory"
              size="sm"
              variant="primary"
              @click="editCategory(category)"
            >
              <b-spinner
                v-if="category.editingCategory"
                small
              />
              <span v-else>Save</span>
            </b-button>
          </div>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="createNewItem(category)"
          >
            Add Item
          </b-button>
          <b-button
            v-if="category.state != 'IN_REVIEW'"
            class="btn-icon rounded-circle"
            variant="flat-danger"
            @click="openDeletePopup(category)"
          >
            <feather-icon
              icon="TrashIcon"
            />
          </b-button>
          <b-button
            v-if="category.state == 'APPROVED' && !category.requestedChange"
            class="btn-icon rounded-circle"
            variant="flat-primary"
            @click="category.editMode = !category.editMode"
          >
            <feather-icon
              :icon="category.editMode ? 'XIcon' : 'EditIcon'"
            />
          </b-button>
        </div>
      </template>
      <b-spinner
        v-if="category.fetchingItems"
        variant="primary"
      />
      <h5 v-if="!category.items.length && !category.fetchingItems">
        No items for this category yet
      </h5>
      <b-row
        v-for="item in category.items"
        :key="item.id"
        class="d-flex align-items-center mb-1"
      >
        <b-col md="4">
          <h4>{{ item.name.en }} / {{ item.name.ar }}</h4>
        </b-col>
        <b-col md="2">
          <h6>
            JOD {{ item.price }}
          </h6>
        </b-col>
        <b-col md="2">
          <h6>{{ snakeCaseToCamelCase(item.state) }}</h6>
          <small class="text-muted">{{ item.requestedChange && '(Pending Changes)' }}</small>
        </b-col>
        <b-col md="2">
          <h6> Created At: {{ formatDate(item.createdAt) }}</h6>
        </b-col>
        <b-col
          md="2"
          class="d-flex"
        >
          <b-button
            class="btn-icon rounded-circle"
            variant="flat-primary"
            @click="item.loadDetails = !item.loadDetails"
          >
            <feather-icon
              :icon="!item.loadDetails ? 'EyeIcon': 'EyeOffIcon'"
            />
          </b-button>
          <b-button
            v-if="category.state != 'IN_REVIEW'"
            class="btn-icon rounded-circle"
            variant="flat-danger"
            @click="openDeleteItemPopup(item)"
          >
            <feather-icon
              icon="TrashIcon"
            />
          </b-button>
          <b-button
            v-if="item.state == 'APPROVED' && !item.requestedChange"
            class="btn-icon rounded-circle"
            variant="flat-primary"
            @click="editItemHandler(category, item)"
          >
            <feather-icon
              icon="EditIcon"
            />
          </b-button>
          <b-button
            class="btn-icon rounded-circle"
            variant="flat-success"
            @click="openItemAvailabilityModal(item)"
          >
            <feather-icon
              icon="BatteryChargingIcon"
            />
          </b-button>
        </b-col>
        <b-col
          v-if="item.loadDetails"
          md="12"
        >
          <item-details :item="item" />
        </b-col>
        <!-- <hr> -->
      </b-row>
    </CardCollapse>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import {
  BSpinner, BRow, BCol, BButton, BFormInput,
} from 'bootstrap-vue'
import CardCollapse from '@/UI/CardCollapse.vue'
import ItemDetails from './ItemDetails.vue'
import snakeCaseToCamelCase from '@/utils/snake-case-to-camcel-caase'
import formatDate from '@/utils/date-format'
import CategoriesForm from './menu-management/CategoriesForm.vue'
import DeleteCategoryModal from './menu-management/DeleteCategoryModal.vue'
import ItemsForm from './menu-management/ItemsForm.vue'
import DeleteItemModal from './menu-management/DeleteItemModal.vue'
import ItemAvailabilityModal from './menu-management/ItemAvailabilityModal.vue'

export default {
  components: {
    CardCollapse,
    ItemAvailabilityModal,
    BSpinner,
    BRow,
    BCol,
    BButton,
    ItemDetails,
    CategoriesForm,
    BFormInput,
    DeleteCategoryModal,
    ItemsForm,
    DeleteItemModal,
  },
  setup(_, ctx) {
    const showCategoriesForm = ref(false)
    const businessId = ctx.root.$route.params.id
    const categories = ref([])
    const fetchingCategories = ref(false)
    const fetchCategories = async () => {
      categories.value = []
      fetchingCategories.value = true
      const res = await ctx.root.$store.dispatch('business/getBusinessCategories', {
        businessId,
      })
      const { data } = res.data.data
      categories.value = data.map(cat => ({
        ...cat,
        fetchingItems: false,
        editMode: false,
        editingCategory: false,
        editModeCat: {
          en: cat.name.en,
          ar: cat.name.ar,
        },
        items: [],
      }))
      fetchingCategories.value = false
    }
    fetchCategories()
    const fetchCategoryItems = async category => {
      // eslint-disable-next-line no-param-reassign
      category.items = []
      // eslint-disable-next-line no-param-reassign
      category.fetchingItems = true
      const res = await ctx.root.$store.dispatch('business/getBusinessItems', {
        businessId,
        categoryId: category.id,
        perPage: 100,
      })
      const { items } = res.data.data.data
      // eslint-disable-next-line no-param-reassign
      category.items = items.map(it => ({
        ...it,
        loadDetails: false,
      }))
      // eslint-disable-next-line no-param-reassign
      category.fetchingItems = false
    }

    const editCategory = async category => {
      try {
        // eslint-disable-next-line no-param-reassign
        category.editingCategory = true
        await ctx.root.$store.dispatch('business/updateCategory', {
          catId: category.id,
          payload: {
            name: category.editModeCat,
          },
        })
        fetchCategories()
      } catch (error) {
        console.log(error)
      } finally {
        // eslint-disable-next-line no-param-reassign
        category.editingCategory = false
      }
    }
    const toBeDeletedCategory = ref({})
    const openDeletePopup = cat => {
      toBeDeletedCategory.value = cat
      ctx.root.$bvModal.show('delete-category-modal')
    }

    const selectedCategory = ref({})
    const createNewItem = category => {
      selectedCategory.value = category
      ctx.root.$bvModal.show('items-form-modal')
    }
    const selectedItem = ref(undefined)
    const editItemHandler = (category, item) => {
      selectedCategory.value = category
      selectedItem.value = JSON.parse(JSON.stringify(item))
      ctx.root.$bvModal.show('items-form-modal')
    }
    const itemsFormHiddenHandler = () => {
      selectedCategory.value = {}
      selectedItem.value = undefined
    }

    const toBeDeletedItem = ref({})
    const openDeleteItemPopup = item => {
      toBeDeletedItem.value = item
      ctx.root.$bvModal.show('delete-item-modal')
    }

    const selectedItemForAvailability = ref({})
    const openItemAvailabilityModal = item => {
      selectedItemForAvailability.value = item
      ctx.root.$bvModal.show('item-availability-modal')
    }

    return {
      openItemAvailabilityModal,
      selectedItemForAvailability,
      createNewItem,
      selectedCategory,
      openDeleteItemPopup,
      editItemHandler,
      openDeletePopup,
      itemsFormHiddenHandler,
      toBeDeletedCategory,
      fetchCategoryItems,
      toBeDeletedItem,
      selectedItem,
      categories,
      snakeCaseToCamelCase,
      formatDate,
      editCategory,
      fetchCategories,
      fetchingCategories,
      showCategoriesForm,
    }
  },
}
</script>

<style>

</style>
